import { useState, useEffect, useCallback } from 'react';

/**
 * Mobile navigation hook.
 *
 * Manages the open/close state of the slide-in sidebar on mobile breakpoints.
 * - Auto-closes when the viewport grows beyond the mobile breakpoint.
 * - Auto-closes when the user navigates (pushState/popstate).
 * - Locks body scroll while the sidebar is open.
 */
export function useMobileNav(breakpoint = 768) {
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Close on resize above breakpoint
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > breakpoint && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint, isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [isOpen]);

  return { isOpen, open, close, toggle };
}
